export interface PaletteFieldItem {
  id: string
  icon: string
  fieldname: string
  keywords?: string
  formInput: Record<string, unknown>
}

export interface PaletteSection {
  id: string
  heading: string
  color: string
  background: string
  borderColor: string
  subComponent: PaletteFieldItem[]
}

const defaultCondition = () => ({
  conditionActive: false,
  basedOnElementOfIndex: null,
  forValue: 'undefined',
  comparisionOperation: '='
})

const defaultValidation = () => [{ rule: 'undefined' }]

const inputField = (
  type: string,
  label: string,
  color: string,
  extra: Record<string, unknown> = {}
) => ({
  type,
  label,
  color,
  placeholder: `Enter ${label.toLowerCase().replace(/:$/, '')}`,
  validation: defaultValidation(),
  includeOnCondition: defaultCondition(),
  ...extra
})

export const buildFormFieldPalette = (themeTextColor: string): PaletteSection[] => [
  {
    id: 'popular',
    heading: 'Popular',
    color: '#16a34a',
    background: '#f0fdf4',
    borderColor: '#bbf7d0',
    subComponent: [
      {
        id: 'plain-input',
        icon: 'bi bi-input-cursor-text',
        fieldname: 'Plain input',
        keywords: 'text input single line plain short answer',
        formInput: inputField('text', 'Plain input', themeTextColor, {
          placeholder: 'Enter text...',
          plainInput: true
        })
      },
      {
        id: 'long-answer',
        icon: 'bi bi-textarea-resize',
        fieldname: 'Long answer',
        keywords: 'textarea paragraph multiline',
        formInput: inputField('textarea', 'Long answer', themeTextColor, {
          rows: 4,
          placeholder: 'Enter your answer'
        })
      },
      {
        id: 'dropdown',
        icon: 'bi bi-menu-button-wide',
        fieldname: 'Dropdown',
        keywords: 'select choice option',
        formInput: {
          type: 'select',
          label: 'Dropdown',
          name: 'dropdown',
          color: themeTextColor,
          options: [
            { value: 'a', text: 'Option A' },
            { value: 'b', text: 'Option B', selected: true },
            { value: 'c', text: 'Option C' }
          ],
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'email',
        icon: 'bi bi-envelope',
        fieldname: 'Email',
        keywords: 'contact mail',
        formInput: inputField('email', 'Email', themeTextColor, {
          placeholder: 'name@example.com'
        })
      },
      {
        id: 'checkbox',
        icon: 'bi bi-check2-square',
        fieldname: 'Checkbox',
        keywords: 'multi select choices',
        formInput: {
          type: 'checkbox',
          label: 'Checkbox',
          color: themeTextColor,
          name: 'checkbox',
          options: [
            { value: 'a', text: 'Option A' },
            { value: 'b', text: 'Option B' },
            { value: 'c', text: 'Option C' }
          ],
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      }
    ]
  },
  {
    id: 'text-inputs',
    heading: 'Text inputs',
    color: '#059669',
    background: '#ecfdf5',
    borderColor: '#a7f3d0',
    subComponent: [
      {
        id: 'text',
        icon: 'bi bi-pencil-square',
        fieldname: 'Plain input',
        keywords: 'text input single line plain short answer',
        formInput: inputField('text', 'Plain input', themeTextColor, {
          placeholder: 'Enter text...',
          plainInput: true
        })
      },
      {
        id: 'textarea',
        icon: 'bi bi-card-text',
        fieldname: 'Long answer',
        formInput: inputField('textarea', 'Long answer', themeTextColor, { rows: 4 })
      },
      {
        id: 'number',
        icon: 'bi bi-123',
        fieldname: 'Number',
        keywords: 'numeric integer decimal',
        formInput: inputField('number', 'Number', themeTextColor, { placeholder: '0' })
      },
      {
        id: 'email-field',
        icon: 'bi bi-envelope-at',
        fieldname: 'Email',
        formInput: inputField('email', 'Email', themeTextColor, { placeholder: 'name@example.com' })
      },
      {
        id: 'phone',
        icon: 'bi bi-telephone',
        fieldname: 'Phone',
        keywords: 'mobile tel contact',
        formInput: inputField('tel', 'Phone number', themeTextColor, {
          placeholder: '+91 98765 43210'
        })
      },
      {
        id: 'url',
        icon: 'bi bi-link-45deg',
        fieldname: 'Website URL',
        keywords: 'link website http',
        formInput: inputField('url', 'Website URL', themeTextColor, {
          placeholder: 'https://example.com'
        })
      },
      {
        id: 'password',
        icon: 'bi bi-shield-lock',
        fieldname: 'Password',
        keywords: 'secret secure',
        formInput: inputField('password', 'Password', themeTextColor, {
          placeholder: 'Enter password'
        })
      },
      {
        id: 'address',
        icon: 'bi bi-geo-alt',
        fieldname: 'Address',
        formInput: inputField('text', 'Address', themeTextColor, {
          placeholder: 'Street, city, state',
          addressInput: true
        })
      }
    ]
  },
  {
    id: 'choices',
    heading: 'Choices',
    color: '#d97706',
    background: '#fffbeb',
    borderColor: '#fde68a',
    subComponent: [
      {
        id: 'select',
        icon: 'bi bi-caret-down-square',
        fieldname: 'Dropdown',
        formInput: {
          type: 'select',
          label: 'Dropdown',
          name: 'dropdown',
          color: themeTextColor,
          options: [
            { value: 'a', text: 'Option A' },
            { value: 'b', text: 'Option B' },
            { value: 'c', text: 'Option C' }
          ],
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'radio',
        icon: 'bi bi-record-circle',
        fieldname: 'Radio',
        formInput: {
          type: 'radio',
          label: 'Radio',
          color: themeTextColor,
          name: 'radio',
          options: [
            { value: 'a', text: 'Option A' },
            { value: 'b', text: 'Option B' },
            { value: 'c', text: 'Option C' }
          ],
          validation: defaultValidation(),
          includeOnCondition: defaultCondition(),
          isDisabled: false
        }
      },
      {
        id: 'checkbox-choice',
        icon: 'bi bi-ui-checks',
        fieldname: 'Checkboxes',
        formInput: {
          type: 'checkbox',
          label: 'Checkboxes',
          color: themeTextColor,
          name: 'checkbox',
          options: [
            { value: 'a', text: 'Option A' },
            { value: 'b', text: 'Option B' },
            { value: 'c', text: 'Option C' }
          ],
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'switch',
        icon: 'bi bi-toggle-on',
        fieldname: 'Switch',
        keywords: 'toggle boolean yes no',
        formInput: {
          type: 'switch',
          label: 'Toggle',
          color: themeTextColor,
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'ratings',
        icon: 'bi bi-star',
        fieldname: 'Ratings',
        formInput: {
          type: 'ratings',
          label: 'Rate & review',
          color: themeTextColor,
          alignment: 'left',
          includeOnCondition: defaultCondition()
        }
      }
    ]
  },
  {
    id: 'date-time',
    heading: 'Date & time',
    color: '#9333ea',
    background: '#faf5ff',
    borderColor: '#e9d5ff',
    subComponent: [
      {
        id: 'date',
        icon: 'bi bi-calendar',
        fieldname: 'Date',
        formInput: inputField('date', 'Date', themeTextColor)
      },
      {
        id: 'time',
        icon: 'bi bi-clock',
        fieldname: 'Time',
        formInput: inputField('time', 'Time', themeTextColor)
      },
      {
        id: 'datetime',
        icon: 'bi bi-calendar-date',
        fieldname: 'Date & time',
        formInput: inputField('datetime', 'Date & time', themeTextColor)
      },
      {
        id: 'daterange',
        icon: 'bi bi-calendar-range',
        fieldname: 'Date range',
        keywords: 'start end period',
        formInput: inputField('daterange', 'Date range', themeTextColor)
      }
    ]
  },
  {
    id: 'display',
    heading: 'Display',
    color: '#64748b',
    background: '#f8fafc',
    borderColor: '#e2e8f0',
    subComponent: [
      {
        id: 'heading',
        icon: 'bi bi-type-h1',
        fieldname: 'Heading',
        formInput: {
          type: 'heading',
          formHeading: 'Heading',
          alignment: 'center',
          color: themeTextColor,
          size: 'h5',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'paragraph',
        icon: 'bi bi-text-paragraph',
        fieldname: 'Paragraph',
        formInput: {
          type: 'subheading',
          formSubHeading: 'Paragraph text goes here.',
          color: themeTextColor,
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'banner',
        icon: 'bi bi-image',
        fieldname: 'Banner',
        formInput: {
          type: 'banner',
          bannerSrc:
            'https://chatbot-project.s3.ap-south-1.amazonaws.com/botgo/assets/Form_Template_01.jpeg',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'divider',
        icon: 'bi bi-hr',
        fieldname: 'Divider',
        formInput: {
          type: 'divider',
          color: '#dee2e6',
          thickness: 1,
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'form-logo',
        icon: 'bi bi-badge-ad',
        fieldname: 'Form logo',
        formInput: {
          type: 'formLogo',
          imgSrc: 'https://devdashboard.botgo.io/media/logos/logo.png',
          alignment: 'center',
          height: 50,
          width: 210,
          includeOnCondition: defaultCondition()
        }
      }
    ]
  },
  {
    id: 'media',
    heading: 'Media & files',
    color: '#0891b2',
    background: '#ecfeff',
    borderColor: '#a5f3fc',
    subComponent: [
      {
        id: 'file',
        icon: 'bi bi-file-earmark-arrow-up',
        fieldname: 'File upload',
        formInput: {
          type: 'file',
          label: 'Upload file',
          color: themeTextColor,
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'signature',
        icon: 'bi bi-pen',
        fieldname: 'Signature',
        formInput: {
          type: 'signature',
          label: 'Signature',
          color: themeTextColor,
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      }
    ]
  },
  {
    id: 'layout',
    heading: 'Layout',
    color: '#0d9488',
    background: '#f0fdfa',
    borderColor: '#99f6e4',
    subComponent: [
      {
        id: 'table',
        icon: 'bi bi-table',
        fieldname: 'Table',
        formInput: {
          type: 'table',
          label: 'Table',
          color: themeTextColor,
          tableRowCount: 3,
          tableColumnCount: 3,
          columnHeaders: ['Column 1', 'Column 2', 'Column 3'],
          columnTypes: ['text', 'text', 'text'],
          showRowHeaders: false,
          rowHeaders: ['Row 1', 'Row 2', 'Row 3'],
          validation: defaultValidation(),
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'subform',
        icon: 'bi bi-layout-text-window-reverse',
        fieldname: 'Sub form',
        formInput: {
          type: 'subform',
          label: 'Sub form',
          color: themeTextColor,
          checked: false,
          subformFields: [
            {
              type: 'switch',
              label: 'Expand',
              color: themeTextColor,
              checked: true,
              includeOnCondition: defaultCondition(),
              fieldId: 2000
            }
          ],
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'add-page',
        icon: 'bi bi-file-earmark-plus',
        fieldname: 'Add page',
        keywords: 'new page multipage',
        formInput: { type: 'addPage' }
      }
    ]
  },
  {
    id: 'buttons',
    heading: 'Buttons',
    color: '#2563eb',
    background: '#eff6ff',
    borderColor: '#bfdbfe',
    subComponent: [
      {
        id: 'btn-submit',
        icon: 'bi bi-check-circle',
        fieldname: 'Submit',
        formInput: {
          type: 'button',
          buttonAction: 'submit',
          text: 'Submit',
          btnTxtColor: '#ffffff',
          btnBgColor: '#198754',
          buttonType: 'submit',
          confirmBeforeAction: true,
          confirmTitle: 'Submit form?',
          confirmMessage: 'Are you sure you want to submit this form?',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'btn-next',
        icon: 'bi bi-arrow-right-circle',
        fieldname: 'Next',
        formInput: {
          type: 'button',
          buttonAction: 'next',
          text: 'Next',
          btnTxtColor: '#ffffff',
          btnBgColor: '#007bff',
          buttonType: 'button',
          confirmBeforeAction: true,
          confirmTitle: 'Continue?',
          confirmMessage: 'Are you sure you want to continue to the next page?',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'btn-previous',
        icon: 'bi bi-arrow-left-circle',
        fieldname: 'Previous',
        formInput: {
          type: 'button',
          buttonAction: 'previous',
          text: 'Previous',
          btnTxtColor: '#ffffff',
          btnBgColor: '#6c757d',
          buttonType: 'button',
          confirmBeforeAction: false,
          confirmTitle: 'Go back?',
          confirmMessage: 'Are you sure you want to go back to the previous page?',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'btn-reset',
        icon: 'bi bi-arrow-counterclockwise',
        fieldname: 'Reset',
        formInput: {
          type: 'button',
          buttonAction: 'reset',
          text: 'Reset',
          btnTxtColor: '#ffffff',
          btnBgColor: '#dc3545',
          buttonType: 'reset',
          confirmBeforeAction: false,
          confirmTitle: 'Reset form?',
          confirmMessage: 'Are you sure you want to reset the form?',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'btn-custom',
        icon: 'bi bi-hand-index',
        fieldname: 'Custom',
        formInput: {
          type: 'button',
          buttonAction: 'custom',
          text: 'Custom button',
          btnTxtColor: '#ffffff',
          btnBgColor: '#6c757d',
          buttonType: 'button',
          confirmBeforeAction: false,
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'btn-popup',
        icon: 'bi bi-window',
        fieldname: 'Popup',
        formInput: {
          type: 'button',
          buttonAction: 'popup',
          text: 'Open popup',
          btnTxtColor: '#ffffff',
          btnBgColor: '#6610f2',
          buttonType: 'button',
          popupTitle: 'Information',
          popupMessage: 'Add your popup message here.',
          popupCloseText: 'Close',
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'btn-popup-actions',
        icon: 'bi bi-chat-square-text',
        fieldname: 'Popup + actions',
        formInput: {
          type: 'button',
          buttonAction: 'popupWithActions',
          text: 'Confirm action',
          btnTxtColor: '#ffffff',
          btnBgColor: '#6610f2',
          buttonType: 'button',
          popupTitle: 'Please confirm',
          popupMessage: 'Are you sure you want to continue?',
          popupButtons: [
            {
              id: 'popup-cancel',
              text: 'Cancel',
              btnTxtColor: '#212529',
              btnBgColor: '#f8f9fa',
              action: 'close'
            },
            {
              id: 'popup-confirm',
              text: 'Confirm',
              btnTxtColor: '#ffffff',
              btnBgColor: '#0d6efd',
              action: 'submit'
            }
          ],
          includeOnCondition: defaultCondition()
        }
      },
      {
        id: 'button-row',
        icon: 'bi bi-segmented-nav',
        fieldname: 'Button row',
        formInput: {
          type: 'buttonRow',
          label: 'Button row',
          alignment: 'end',
          buttons: [
            {
              id: 'row-prev',
              buttonAction: 'previous',
              text: 'Previous',
              btnTxtColor: '#ffffff',
              btnBgColor: '#6c757d',
              buttonType: 'button',
              confirmBeforeAction: false,
              confirmTitle: 'Go back?',
              confirmMessage: 'Are you sure you want to go back to the previous page?'
            },
            {
              id: 'row-next',
              buttonAction: 'next',
              text: 'Next',
              btnTxtColor: '#ffffff',
              btnBgColor: '#007bff',
              buttonType: 'button',
              confirmBeforeAction: true,
              confirmTitle: 'Continue?',
              confirmMessage: 'Are you sure you want to continue to the next page?'
            }
          ],
          includeOnCondition: defaultCondition()
        }
      }
    ]
  },
  {
    id: 'security',
    heading: 'Security',
    color: '#dc2626',
    background: '#fef2f2',
    borderColor: '#fecaca',
    subComponent: [
      {
        id: 'captcha',
        icon: 'bi bi-shield-check',
        fieldname: 'Captcha',
        formInput: {
          type: 'captcha',
          label: 'Captcha',
          color: themeTextColor,
          includeOnCondition: defaultCondition()
        }
      }
    ]
  }
]
